/**
 * Fields the import can fill, grouped by wizard section.
 *
 * Mirrors TARGETS and AUTO_FLAGS in backend/service_client_forms/cashflow_import/categories.py;
 * tests/test_cashflow_import.py::test_frontend_targets_match_backend keeps them in step.
 * Two targets are special: "income.source" adds an Income source (name + gross amount) and
 * "auto_transport.auto_loan" fills the first free Auto Lease / Loan slot.
 */
export const IMPORT_TARGET_SECTIONS = [
  {
    "section": "income",
    "label": "Income",
    "fields": [
      {
        "field": "source",
        "label": "Income source (gross)"
      }
    ]
  },
  {
    "section": "housing",
    "label": "Housing",
    "fields": [
      {
        "field": "mortgage_or_rent",
        "label": "Mortgage (Principal & Interest) or Rent"
      },
      {
        "field": "association_dues",
        "label": "Association Dues (HOA, Condo, etc.)"
      },
      {
        "field": "home_maintenance",
        "label": "Ongoing Home Maintenance"
      },
      {
        "field": "household_cleaning",
        "label": "Household Cleaning & Other Services"
      },
      {
        "field": "landscaping",
        "label": "Landscaping & Lawn Care Services"
      },
      {
        "field": "home_security",
        "label": "Home Security System"
      },
      {
        "field": "escrow",
        "label": "Escrow"
      },
      {
        "field": "property_taxes",
        "label": "Property Taxes"
      },
      {
        "field": "homeowners_insurance",
        "label": "Homeowners / Renters Insurance"
      },
      {
        "field": "pmi",
        "label": "Private Mortgage Insurance (PMI)"
      }
    ]
  },
  {
    "section": "bills_utilities",
    "label": "Bills & Utilities",
    "fields": [
      {
        "field": "electric",
        "label": "Electric"
      },
      {
        "field": "gas",
        "label": "Gas"
      },
      {
        "field": "internet_cable",
        "label": "Internet / Cable"
      },
      {
        "field": "phone_mobile",
        "label": "Phone / Mobile"
      },
      {
        "field": "trash_recycling",
        "label": "Trash / Recycling"
      },
      {
        "field": "water_sewer",
        "label": "Water / Sewer"
      }
    ]
  },
  {
    "section": "auto_transport",
    "label": "Auto & Transport",
    "fields": [
      {
        "field": "gas_fuel",
        "label": "Gas / Fuel"
      },
      {
        "field": "license_registration",
        "label": "License & Registration"
      },
      {
        "field": "parking_tolls",
        "label": "Parking & Tolls"
      },
      {
        "field": "service_maintenance",
        "label": "Service & Maintenance (tires, oil, etc.)"
      },
      {
        "field": "auto_insurance",
        "label": "Auto Insurance"
      },
      {
        "field": "public_transportation",
        "label": "Public Transportation"
      },
      {
        "field": "auto_loan",
        "label": "Auto Lease / Loan Payment"
      }
    ]
  },
  {
    "section": "medical",
    "label": "Medical",
    "fields": [
      {
        "field": "dental_copays",
        "label": "Dental Copays & Expenses"
      },
      {
        "field": "medical_copays",
        "label": "Medical Copays & Expenses"
      },
      {
        "field": "vision_copays",
        "label": "Vision Copays & Expenses (glasses, contacts)"
      },
      {
        "field": "pharmacy_medications",
        "label": "Pharmacy & Medications"
      },
      {
        "field": "alternative_health_care",
        "label": "Alternative / Other Health Care"
      },
      {
        "field": "dental_insurance",
        "label": "Dental Insurance"
      },
      {
        "field": "medical_insurance",
        "label": "Medical Insurance"
      },
      {
        "field": "vision_insurance",
        "label": "Vision Insurance"
      },
      {
        "field": "prescription_insurance",
        "label": "Prescription Insurance / Medicare Part D"
      }
    ]
  },
  {
    "section": "insurance",
    "label": "Insurance",
    "fields": [
      {
        "field": "life_insurance",
        "label": "Life Insurance (Term, Universal, etc.)"
      },
      {
        "field": "long_term_care",
        "label": "Long-Term Care Insurance"
      },
      {
        "field": "disability",
        "label": "Disability Insurance (Long-Term / Short-Term)"
      },
      {
        "field": "umbrella",
        "label": "Umbrella Insurance"
      },
      {
        "field": "other_insurance",
        "label": "Other Insurance"
      }
    ]
  },
  {
    "section": "entertainment_travel",
    "label": "Entertainment & Travel",
    "fields": [
      {
        "field": "books_magazines_movies_music",
        "label": "Books, Magazines, Movies & Music"
      },
      {
        "field": "concerts_events",
        "label": "Concerts & Events"
      },
      {
        "field": "sports_hobbies_activities",
        "label": "Sports, Hobbies & Activities"
      },
      {
        "field": "streaming_subscriptions",
        "label": "Streaming Services & Subscriptions"
      },
      {
        "field": "travel_vacation",
        "label": "Travel & Vacation"
      },
      {
        "field": "miscellaneous_entertainment",
        "label": "Miscellaneous Entertainment"
      }
    ]
  },
  {
    "section": "personal_family_care",
    "label": "Personal & Family Care",
    "fields": [
      {
        "field": "groceries_household",
        "label": "Groceries & Household Supplies"
      },
      {
        "field": "restaurants_dining",
        "label": "Restaurants & Dining"
      },
      {
        "field": "gifts",
        "label": "Gifts"
      },
      {
        "field": "clothing_shoes",
        "label": "Clothing & Shoes"
      },
      {
        "field": "hair_skin_nails",
        "label": "Hair, Skin & Nail Care"
      },
      {
        "field": "pocket_money_cash",
        "label": "Pocket Money / Cash"
      },
      {
        "field": "technology_computers_devices",
        "label": "Technology, Computers & Mobile Devices"
      },
      {
        "field": "dues_memberships",
        "label": "Dues & Memberships"
      }
    ]
  },
  {
    "section": "kids_education",
    "label": "Kids & Education",
    "fields": [
      {
        "field": "child_support",
        "label": "Child Support"
      },
      {
        "field": "books_school_supplies",
        "label": "Books & School Supplies"
      },
      {
        "field": "clothing_toys_supplies",
        "label": "Clothing, Toys & Other Supplies"
      },
      {
        "field": "daycare_childcare_sitters",
        "label": "Day Care / Child Care / Sitters"
      },
      {
        "field": "k12_tuition",
        "label": "K-12 Education / Tuition"
      },
      {
        "field": "college_tuition",
        "label": "College Tuition"
      }
    ]
  },
  {
    "section": "pets",
    "label": "Pets",
    "fields": [
      {
        "field": "food_supplies",
        "label": "Food & Supplies"
      },
      {
        "field": "grooming",
        "label": "Grooming"
      },
      {
        "field": "boarding_pet_sitting",
        "label": "Boarding / Pet Sitting Fees"
      },
      {
        "field": "vet",
        "label": "Vet"
      }
    ]
  },
  {
    "section": "charity",
    "label": "Charity",
    "fields": [
      {
        "field": "charitable_gifting",
        "label": "Charitable Gifting"
      },
      {
        "field": "tithes_church",
        "label": "Tithes / Church"
      }
    ]
  },
  {
    "section": "legal_professional",
    "label": "Legal / Professional",
    "fields": [
      {
        "field": "cpa_accountant",
        "label": "CPA / Accountant"
      },
      {
        "field": "other_expenses",
        "label": "Other Legal / Professional Expenses"
      }
    ]
  },
  {
    "section": "savings",
    "label": "Savings",
    "fields": [
      {
        "field": "retirement_savings",
        "label": "Retirement Savings (401(k), IRA, etc.)"
      },
      {
        "field": "hsa_fsa",
        "label": "HSA and/or FSA Savings"
      },
      {
        "field": "education_savings_529",
        "label": "Education Savings (529s, College Accounts, etc.)"
      },
      {
        "field": "brokerage_taxable",
        "label": "Brokerage / Taxable Savings"
      },
      {
        "field": "other_savings",
        "label": "Other Savings"
      }
    ]
  },
  {
    "section": "debts",
    "label": "Debts",
    "fields": [
      {
        "field": "credit_cards",
        "label": "Credit Card(s)"
      },
      {
        "field": "home_equity_line_of_credit",
        "label": "Home Equity or Personal Line(s) of Credit"
      },
      {
        "field": "student_loans",
        "label": "Student Loan(s)"
      },
      {
        "field": "other_debts",
        "label": "Other Debts"
      }
    ]
  }
];

/** Field -> its "deducted from wages" checkbox, set when a pay stub or benefits summary says so. */
export const IMPORT_AUTO_FLAGS = {
  "medical.dental_insurance": "dental_insurance_auto",
  "medical.medical_insurance": "medical_insurance_auto",
  "medical.vision_insurance": "vision_insurance_auto",
  "medical.prescription_insurance": "prescription_insurance_auto",
  "insurance.life_insurance": "life_insurance_auto",
  "insurance.long_term_care": "long_term_care_auto",
  "insurance.disability": "disability_auto",
  "insurance.umbrella": "umbrella_auto",
  "insurance.other_insurance": "other_insurance_auto",
  "savings.retirement_savings": "retirement_savings_auto",
  "savings.hsa_fsa": "hsa_fsa_auto"
};

export const INCOME_SOURCE = 'income.source';
export const AUTO_LOAN = 'auto_transport.auto_loan';

/** "section.field" -> { section, field, label, sectionLabel } */
export const IMPORT_TARGETS = Object.fromEntries(
  IMPORT_TARGET_SECTIONS.flatMap((s) =>
    s.fields.map((f) => [`${s.section}.${f.field}`, { section: s.section, field: f.field, label: f.label, sectionLabel: s.label }]),
  ),
);
